import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import toast from "react-hot-toast";

import { axiosConfig } from "../../axios/axiosConfig";
import ReactHotToaster from "../../components/common/ReactHotToaster";

import QrCode2Icon from "@mui/icons-material/QrCode2";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// ── palette (brand) ──────────────────────────────────────────────────────
const navy = "#04343a";
const brandGreen = "#016551";
const negRed = "#dc2626";
const cardBorder = "#e2e8f0";
const textMain = "#1e293b";
const textMuted = "#64748b";

const STATUS_COLORS = { active: brandGreen, closed: textMuted, expired: negRed };

const LIST_COLUMNS = [
  "ID", "User", "Plan", "Amount", "Status", "QR Code", "Received", "Created By", "Close Reason", "Created At", "Action",
];

const Chip = ({ label, color }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      background: color,
      borderRadius: "999px",
      padding: "3px 10px",
      whiteSpace: "nowrap",
      textTransform: "capitalize",
    }}
  >
    {label}
  </Box>
);

const fmtRs = (n, currency) =>
  n === null || n === undefined ? "—" : `${currency === "INR" ? "₹" : currency + " "}${Number(n).toLocaleString("en-IN")}`;

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
};

const LIST_URL = "/payment/list-qr-codes/";

const initialForm = {
  customer_no: "",
  plan_id: "",
  amount: "",
};

const initialCustomer = {
  loading: false,
  userId: null,
  username: null,
  error: "",
};

const CreateQR = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [customer, setCustomer] = useState(initialCustomer);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [createdQr, setCreatedQr] = useState(null);

  const [qrCodes, setQrCodes] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);
  const [search, setSearch] = useState("");
  const [cancellingId, setCancellingId] = useState(null);
  const [qrPreview, setQrPreview] = useState(null); // { image, username, plan_name }

  const loadQrCodes = async (url = LIST_URL) => {
    setListLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url });
      setQrCodes(Array.isArray(res.data?.results?.response) ? res.data.results.response : []);
      setCount(res.data?.count ?? 0);
      setNextUrl(res.data?.next || null);
      setPreviousUrl(res.data?.previous || null);
    } catch (error) {
      console.log("list-qr-codes error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load QR codes");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadQrCodes();
  }, []);

  const filteredQrCodes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return qrCodes;
    return qrCodes.filter((l) => Object.values(l || {}).some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [qrCodes, search]);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: "/payment/plans" });
      setPlans(res?.data?.response?.data || res?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.response || error.message || "Failed to load plans");
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchPlans();
  }, [open]);

  const handleOpen = () => {
    setForm(initialForm);
    setCustomer(initialCustomer);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm(initialForm);
    setCustomer(initialCustomer);
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (field === "customer_no") {
      setCustomer(initialCustomer);
    }
  };

  const handleFetchCustomer = async () => {
    const phoneno = form.customer_no.trim();
    if (!phoneno) {
      toast.error("Please enter a customer number");
      return;
    }
    setCustomer({ ...initialCustomer, loading: true });
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/user-plan-details",
        params: { phoneno },
      });
      const userDetails = res?.data?.result?.user_details;
      const matchedUser = Array.isArray(userDetails) ? userDetails[0] : null;
      if (!matchedUser?.id) {
        setCustomer({ ...initialCustomer, error: "No user found for this number" });
        toast.error("No user found for this number");
        return;
      }
      setCustomer({
        loading: false,
        userId: matchedUser.id,
        username: matchedUser.username || matchedUser.name || phoneno,
        error: "",
      });
      toast.success("Customer found");
    } catch (error) {
      const errMsg = error?.response?.data?.response;
      const message = typeof errMsg === "string" ? errMsg : "Failed to fetch customer details";
      setCustomer({ ...initialCustomer, error: message });
      toast.error(message);
    }
  };

  const isValid =
    !!customer.userId &&
    form.plan_id !== "" &&
    form.amount.toString().trim() !== "";

  const handleSubmit = async () => {
    if (!customer.userId) {
      toast.error("Please fetch and select a valid customer first");
      return;
    }
    if (!isValid) {
      toast.error("Please fill all the fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "/payment/create-qr-code/",
        data: {
          user_id: customer.userId,
          plan_id: Number(form.plan_id),
          amount: Number(form.amount),
        },
      });
      const data = res?.data?.response || res?.data || {};
      const qrImage = data?.image_url || null;
      setCreatedQr({ image: qrImage, link: qrImage, raw: data });

      const successMsg =
        typeof res?.data?.response === "string"
          ? res.data.response
          : typeof res?.data?.message === "string"
            ? res.data.message
            : "QR code created successfully";
      toast.success(successMsg);
      handleClose();
      loadQrCodes();
    } catch (error) {
      const errMsg = error?.response?.data?.response;
      toast.error(typeof errMsg === "string" ? errMsg : error?.message || "Failed to create QR code");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelQr = async (qrCodeId) => {
    setCancellingId(qrCodeId);
    try {
      await axiosConfig({
        method: "post",
        url: "/payment/close-qr-code/",
        data: { qr_code_id: qrCodeId },
      });
      toast.success("QR code cancelled successfully");
      loadQrCodes();
    } catch (error) {
      const errMsg = error?.response?.data?.response;
      toast.error(typeof errMsg === "string" ? errMsg : error?.message || "Failed to cancel QR code");
    } finally {
      setCancellingId(null);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(createdQr?.link || "");
      toast.success("Link copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <QrCode2Icon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Create QR
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              Generate payment QR codes
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box
            component="button"
            onClick={() => loadQrCodes()}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px",
              padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,0.18)" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            Create QR
          </Button>
        </Box>
      </Box>

      {/* Last created QR */}
      {createdQr && (
        <Box
          sx={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
            p: 3,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {createdQr.image && (
            <Box
              component="img"
              src={createdQr.image}
              alt="Payment QR"
              sx={{ width: 140, height: 140, borderRadius: "8px", border: "1px solid #e2e8f0" }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#04343a", mb: 0.5 }}>
              QR Code Created
            </Typography>
            {createdQr.link ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: "#016551", wordBreak: "break-all" }}>
                  {createdQr.link}
                </Typography>
                <IconButton size="small" onClick={handleCopyLink} sx={{ color: "#016551" }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                {JSON.stringify(createdQr.raw)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Search */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search by user, plan, status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{ background: "#fff", borderRadius: "8px", minWidth: 280 }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ background: "#fff", border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <Box component="thead">
              <Box component="tr">
                {LIST_COLUMNS.map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
                      textTransform: "uppercase", letterSpacing: "0.5px",
                      background: navy, p: "8px 10px", whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {listLoading ? (
                <Box component="tr">
                  <Box component="td" colSpan={LIST_COLUMNS.length} sx={{ p: 4, textAlign: "center" }}>
                    <CircularProgress size={26} sx={{ color: brandGreen }} />
                  </Box>
                </Box>
              ) : filteredQrCodes.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={LIST_COLUMNS.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                    No QR codes found
                  </Box>
                </Box>
              ) : (
                filteredQrCodes.map((q, i) => (
                  <Box component="tr" key={q.id} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{q.id}</Box>
                    <Box component="td" sx={{ p: "6px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textMain }}>{q.username || "—"}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: textMuted }}>ID: {q.user_id}</Typography>
                    </Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12.5, color: textMain, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{q.plan_name || "—"}</Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(q.amount, q.currency)}</Box>
                    <Box component="td" sx={{ p: "6px 10px", borderBottom: "1px solid #f1f5f9" }}>
                      <Chip label={q.status} color={STATUS_COLORS[q.status] || textMuted} />
                    </Box>
                    <Box component="td" sx={{ p: "6px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {q.image_url ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            component="span"
                            onClick={() => setQrPreview({ image: q.image_url, username: q.username, plan_name: q.plan_name })}
                            sx={{
                              fontSize: 12.5, color: brandGreen, fontWeight: 700,
                              display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                              "&:hover": { color: "#014d3d", textDecoration: "underline" },
                            }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 13 }} /> View
                          </Box>
                          <Box sx={{ width: "1px", height: "12px", background: cardBorder }} />
                          <Box
                            component="span"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(q.image_url);
                                toast.success("Link copied to clipboard");
                              } catch (error) {
                                toast.error("Failed to copy link");
                              }
                            }}
                            sx={{
                              fontSize: 12.5, color: textMuted, fontWeight: 700,
                              display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                              "&:hover": { color: textMain, textDecoration: "underline" },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 13 }} /> Copy
                          </Box>
                        </Box>
                      ) : (
                        "—"
                      )}
                    </Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {q.payments_count_received ?? 0} ({fmtRs(q.payments_amount_received, q.currency)})
                    </Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{q.created_by || "—"}</Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{q.close_reason || "—"}</Box>
                    <Box component="td" sx={{ p: "6px 10px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtDate(q.created_at)}</Box>
                    <Box component="td" sx={{ p: "6px 10px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                      {q.status === "active" ? (
                        <Box
                          component="button"
                          onClick={() => handleCancelQr(q.qr_code_id)}
                          disabled={cancellingId === q.qr_code_id}
                          sx={{
                            display: "inline-flex", alignItems: "center", gap: 0.5,
                            background: negRed, color: "#fff", border: "none", borderRadius: "6px",
                            padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                            "&:hover": { background: "#b91c1c" },
                            "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                          }}
                        >
                          {cancellingId === q.qr_code_id ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : "Cancel QR"}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: textMuted }}>—</Typography>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "8px 10px", borderTop: `1px solid ${cardBorder}` }}>
          <Typography sx={{ fontSize: 12, color: textMuted }}>{count} total QR code{count === 1 ? "" : "s"}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton size="small" disabled={!previousUrl || listLoading} onClick={() => loadQrCodes(previousUrl)} sx={{ color: textMain }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" disabled={!nextUrl || listLoading} onClick={() => loadQrCodes(nextUrl)} sx={{ color: textMain }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Create QR Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "12px", position: "relative" } }}
      >
        <DialogTitle sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "18px", pb: 2 }}>
          Create QR
        </DialogTitle>
        <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              label="Customer No"
              placeholder="Enter customer phone number"
              fullWidth
              margin="dense"
              value={form.customer_no}
              onChange={handleChange("customer_no")}
            />
            <Button
              variant="outlined"
              onClick={handleFetchCustomer}
              disabled={customer.loading || !form.customer_no.trim()}
              sx={{ mt: "12px", height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {customer.loading ? <CircularProgress size={16} /> : "Fetch"}
            </Button>
          </Box>
          {customer.userId ? (
            <Typography sx={{ fontSize: 12.5, color: brandGreen, fontWeight: 600, mt: -1.5 }}>
              Customer found: {customer.username} (ID: {customer.userId})
            </Typography>
          ) : customer.error ? (
            <Typography sx={{ fontSize: 12.5, color: negRed, fontWeight: 600, mt: -1.5 }}>
              {customer.error}
            </Typography>
          ) : null}
          <TextField
            select
            label="Plan"
            fullWidth
            margin="dense"
            value={form.plan_id}
            onChange={handleChange("plan_id")}
            SelectProps={{
              MenuProps: { PaperProps: { style: { maxHeight: 320 } } },
            }}
            InputProps={{
              endAdornment: plansLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null,
            }}
          >
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name
                  ? `${plan.name} (${plan.interval} ${plan.interval_unit})${plan.plan_code ? ` [${plan.plan_code}]` : ""}${plan.price ? ` — ₹${plan.price}` : ""}`
                  : `Plan #${plan.id}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            type="number"
            fullWidth
            margin="dense"
            value={form.amount}
            onChange={handleChange("amount")}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            sx={{
              background: "linear-gradient(135deg, #016551, #014d3d)",
              borderRadius: "8px",
              fontWeight: 600,
              px: 4,
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              "&:disabled": { background: "#ccc" },
            }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!qrPreview} onClose={() => setQrPreview(null)} maxWidth="xs" fullWidth>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: navy, p: "12px 16px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
            {qrPreview?.username || "Payment QR"}
            {qrPreview?.plan_name ? ` — ${qrPreview.plan_name}` : ""}
          </Typography>
          <IconButton size="small" onClick={() => setQrPreview(null)} sx={{ color: "#fff" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          {qrPreview && (
            <Box
              component="img"
              src={qrPreview.image}
              alt="Payment QR"
              sx={{ width: "100%", maxWidth: 320, borderRadius: "8px", border: "1px solid #e2e8f0" }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ReactHotToaster />
    </Box>
  );
};

export default CreateQR;
